import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, Select, Button } from "antd";
import Card from "./vendor-card/VendorCard";
import WeddingPagination from "./WeddingPagination";
import LoadingSpinner from "components/UI/loading-spinner/LoadingSpinner";
import useWindowWidth from "helpers/useWindowWidth";
import SEO from "components/SEO/SEO";
import styles from "./VendorsList.module.css";
import { API_BASE_URL, GOOGLE_MAP_API_KEY } from "configs/AppConfig";
import { SearchOutlined } from "@ant-design/icons";
import Autocomplete from "react-google-autocomplete";
import { useSelector } from "react-redux";

const { Option } = Select;
const parseNumber = (value, fallback) => {
  const n = Number(value);
  return isNaN(n) || n <= 0 ? fallback : n;
};

const VendorsList = () => {
  const width = useWindowWidth();
  const defaultLimit =
    width > 1600 ? 10 : width > 1200 ? 8 : width > 900 ? 6 : 10;

  const navigate = useNavigate();
  const { search: qs } = useLocation();
  const query = useMemo(() => new URLSearchParams(qs), [qs]);

  const [currentPage, setCurrentPage] = useState(
    parseNumber(query.get("page"), 1)
  );
  const [limit, setLimit] = useState(
    parseNumber(query.get("limit"), defaultLimit)
  );
  const [searchTerm, setSearchTerm] = useState(query.get("search") || "");
  const [locationFilter, setLocationFilter] = useState(
    query.get("location") || ""
  );
  const [selectedLat, setSelectedLat] = useState(query.get("latitude") || "");
  const [selectedLng, setSelectedLng] = useState(query.get("longitude") || "");
  const [categoryFilter, setCategoryFilter] = useState(
    query.get("category") || ""
  );

  const [vendors, setVendors] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(
    query.get("location") || ""
  );
  const inputRef = React.useRef(null);
  const { homeApis } = useSelector((state) => state.widgets);
  const [clearLoading, setClearLoading] = useState(false);

  const pushState = (overrides = {}) => {
    const params = new URLSearchParams();

    params.set("page", overrides.page ?? currentPage);
    params.set("limit", overrides.limit ?? limit);

    const search = overrides.search ?? searchTerm;
    const location = overrides.location ?? locationFilter;
    const category = overrides.category ?? categoryFilter;
    const latitude = overrides.latitude ?? selectedLat;
    const longitude = overrides.longitude ?? selectedLng;

    if (search) params.set("search", search);
    if (location) params.set("location", location);
    if (category) params.set("category", category);
    if (latitude) params.set("latitude", latitude);
    if (longitude) params.set("longitude", longitude);

    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  useEffect(() => {
    pushState();
  }, [
    currentPage,
    limit,
    locationFilter,
    categoryFilter,
    selectedLat,
    selectedLng,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      setVendors([]);
      setLoading(true);
      setError(null);
      try {
        const endpoint = new URL(`${API_BASE_URL}user/public/vendors`);
        endpoint.searchParams.set("page", currentPage);
        endpoint.searchParams.set("limit", limit);
        if (searchTerm) endpoint.searchParams.set("search", searchTerm);
        // if (locationFilter)
        //   endpoint.searchParams.set("location", locationFilter);
        if (selectedLat && selectedLng) {
          endpoint.searchParams.set("latitude", selectedLat);
          endpoint.searchParams.set("longitude", selectedLng);
        }
        if (categoryFilter)
          endpoint.searchParams.set("categoryId", categoryFilter);

        const res = await fetch(endpoint.toString());
        if (!res.ok) throw new Error("Failed to fetch data");

        const json = await res.json();
        setVendors(json?.data ?? []);
        setTotalItems(json?.total ?? 0);
      } catch (err) {
        setError(err.message);
        setVendors([]);
      } finally {
        setLoading(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        setClearLoading(false);
      }
    };
    fetchData();
  }, [qs]);

  const clearFilters = () => {
    if (
      searchTerm ||
      locationFilter ||
      categoryFilter ||
      selectedLat ||
      selectedLng
    ) {
      setClearLoading(true);
    }
    setSearchTerm("");
    setLocationFilter("");
    setCategoryFilter("");
    setSelectedLat("");
    setSelectedLng("");
    setCurrentPage(1);
    setSelectedAddress("");

    const params = new URLSearchParams();
    params.set("page", "1");
    params.set("limit", String(limit));
    navigate({ search: `?${params.toString()}` }, { replace: true });
    // setTimeout(() => setClearLoading(false), 1000);
  };

 const locationFinder = async (place) => {
  if (!place.name) return;
  if (place.geometry?.location) {
    const latitude = place.geometry.location.lat();
    const longitude = place.geometry.location.lng();
    const formattedAddress = `${place.name}, ${place.formatted_address}`;

    setSelectedAddress(formattedAddress);
    setLocationFilter(formattedAddress);
    setSelectedLat(latitude);
    setSelectedLng(longitude);

    // 🔥 set page and update state BEFORE pushState gets triggered
    setCurrentPage(1);

    // ✅ Important: delay navigation slightly to ensure state updates first
    setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      params.set("latitude", latitude);
      params.set("longitude", longitude);
      params.set("location", formattedAddress);
      params.set("page", "1");
      navigate({ search: `?${params.toString()}` }, { replace: true });
    }, 0);
  }
};

  const handleSearchIconClick = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("search", searchTerm);
    params.set("page", 1);
    setCurrentPage(1);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  return (
    <>
      <SEO title="Vendors" />

      <div className={styles.container}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Vendors</h1>
          </div>
          <div className={`${styles.filters} location-vendor`}>
            <Input
              allowClear={false}
              placeholder="Search by Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              onPressEnter={handleSearchIconClick}
              suffix={
                <SearchOutlined
                  onClick={handleSearchIconClick}
                  style={{ cursor: "pointer", color: "#aaa" }}
                />
              }
            />

            <div className={styles.locationAutocomplete}>
              <Autocomplete
                className="custom-autocomplete"
                inputRef={inputRef}
                onChange={(e) => setSelectedAddress(e.target.value)}
                value={selectedAddress}
                style={{
                  width: width > 1200 ? "260px" : "100%",
                  borderRadius: "22px",
                  height: "40px",
                  padding: "0 26px",
                  border: "1px solid #d9d9d9",
                  fontSize: "14px",
                  color: "#0d0a19",
                }}
                apiKey={GOOGLE_MAP_API_KEY}
                onPlaceSelected={locationFinder}
                options={{
                  types: ["establishment", "geocode"],
                  fields: [
                    "name",
                    "formatted_address",
                    "geometry",
                    "address_components",
                    "place_id",
                    "vicinity",
                  ],
                  componentRestrictions: { country: "in" },
                }}
              />
            </div>

            <Select
              allowClear
              showSearch
              placeholder="Category"
              value={categoryFilter || undefined}
              onChange={(val) => {
                const params = new URLSearchParams(window.location.search);
                params.set("page", 1);
                setCurrentPage(1);
                setCategoryFilter(val || "");
              }}
              style={{ width: 160 }}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {homeApis?.category?.map((category) => (
                <Option key={category._id} value={category._id}>
                  {category.name}
                </Option>
              ))}
            </Select>

            <Button
              className={styles.clearButton}
              danger
              onClick={clearFilters}
              loading={clearLoading}
              disabled={clearLoading}
            >
              Clear
            </Button>
          </div>
        </header>

        {loading && vendors.length === 0 && <LoadingSpinner />}
        {error && <h4 className="home-error">{error}</h4>}
        {!loading && vendors.length === 0 && !error && (
          <h4 className="home-error">No Vendors Found</h4>
        )}
        {!loading && vendors.length > 0 && (
          <>
            <div className={styles.grid}>
              {vendors.map((vendor) => (
                <Card
                  key={vendor?.id}
                  id={vendor?.id}
                  image={vendor?.profileImage}
                  title={vendor?.name}
                  location={vendor?.location?.locationName}
                  category={vendor.categories}
                  rating={vendor?.rating}
                />
              ))}
            </div>

            <div className={styles.pagination}>
              <WeddingPagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalItemsCount={totalItems}
                itemsCountPerPage={limit}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default VendorsList;
